import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

export type ColorScheme = 'dark' | 'light' | 'system'

interface ThemeContextType {
  accentColor: string
  setAccentColor: (color: string) => void
  colorScheme: ColorScheme
  setColorScheme: (scheme: ColorScheme) => void
  /** The actual resolved theme (dark or light) after system preference */
  resolvedTheme: 'dark' | 'light'
  isSettingsOpen: boolean
  openSettings: () => void
  closeSettings: () => void
}

const ThemeContext = createContext<ThemeContextType | null>(null)

const ACCENT_STORAGE_KEY = 'statscout_accent_color'
const SCHEME_STORAGE_KEY = 'statscout_color_scheme'
const DEFAULT_ACCENT = '#7c5cff'
const DEFAULT_SCHEME: ColorScheme = 'dark'

// Preset accent colors
export const ACCENT_PRESETS = [
  { name: 'Purple', color: '#7c5cff' },
  { name: 'Blue', color: '#3b82f6' },
  { name: 'Cyan', color: '#06b6d4' },
  { name: 'Teal', color: '#14b8a6' },
  { name: 'Green', color: '#22c55e' },
  { name: 'Yellow', color: '#eab308' },
  { name: 'Orange', color: '#f97316' },
  { name: 'Red', color: '#ef4444' },
  { name: 'Pink', color: '#ec4899' },
  { name: 'Rose', color: '#f43f5e' },
]

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

function applyAccentColor(color: string) {
  const rgb = hexToRgb(color)
  if (!rgb) return

  const root = document.documentElement
  root.style.setProperty('--accent', color)
  root.style.setProperty('--accent-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`)
  // Also set accent glow
  root.style.setProperty('--accent-glow', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`)
}

function applyColorScheme(theme: 'dark' | 'light') {
  const root = document.documentElement
  root.setAttribute('data-theme', theme)

  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]')
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme === 'dark' ? '#0c0c0e' : '#f5f5f7')
  }
}

function getSystemPreference(): 'dark' | 'light' {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [accentColor, setAccentColorState] = useState<string>(() => {
    if (typeof window === 'undefined') return DEFAULT_ACCENT
    const stored = localStorage.getItem(ACCENT_STORAGE_KEY)
    return stored || DEFAULT_ACCENT
  })

  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(() => {
    if (typeof window === 'undefined') return DEFAULT_SCHEME
    const stored = localStorage.getItem(SCHEME_STORAGE_KEY) as ColorScheme | null
    return stored || DEFAULT_SCHEME
  })

  const [systemPreference, setSystemPreference] = useState<'dark' | 'light'>(() => getSystemPreference())
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  // Compute resolved theme
  const resolvedTheme = colorScheme === 'system' ? systemPreference : colorScheme

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handler = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  // Apply accent color
  useEffect(() => {
    applyAccentColor(accentColor)
  }, [accentColor])

  // Apply color scheme
  useEffect(() => {
    applyColorScheme(resolvedTheme)
  }, [resolvedTheme])

  // Apply on initial load
  useEffect(() => {
    applyAccentColor(accentColor)
    applyColorScheme(resolvedTheme)
  }, [])

  const setAccentColor = useCallback((color: string) => {
    setAccentColorState(color)
    localStorage.setItem(ACCENT_STORAGE_KEY, color)
  }, [])

  const setColorScheme = useCallback((scheme: ColorScheme) => {
    setColorSchemeState(scheme)
    localStorage.setItem(SCHEME_STORAGE_KEY, scheme)
  }, [])

  const openSettings = useCallback(() => setIsSettingsOpen(true), [])
  const closeSettings = useCallback(() => setIsSettingsOpen(false), [])

  return (
    <ThemeContext.Provider
      value={{
        accentColor,
        setAccentColor,
        colorScheme,
        setColorScheme,
        resolvedTheme,
        isSettingsOpen,
        openSettings,
        closeSettings,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
